@if (markedTotal > 0) {
	<div class="sm-audit-strip sm-audit-strip-warn" role="status">
		<span class="sm-badge sm-badge-warn">{{ 'administration.sync-monitoring.audit.marked-strip' | translate: { count: markedTotal } }}</span>
		<span>{{ 'administration.sync-monitoring.audit.marked-strip-text' | translate }}</span>
	</div>
}

<details class="sm-audit-how">
	<summary>{{ 'administration.sync-monitoring.audit.how.title' | translate }}</summary>
	<ol>
		<li>{{ 'administration.sync-monitoring.audit.how.find' | translate }}</li>
		<li>{{ 'administration.sync-monitoring.audit.how.replay' | translate }}</li>
		<li>{{ 'administration.sync-monitoring.audit.how.cap' | translate }}</li>
		<li>{{ 'administration.sync-monitoring.audit.how.verify' | translate }}</li>
		<li>{{ 'administration.sync-monitoring.audit.how.exhausted' | translate }}</li>
	</ol>
</details>

@if (tableSource.data.length === 0) {
	<div class="sm-muted">{{ 'administration.sync-monitoring.tables.empty' | translate }}</div>
} @else {
	<app-generictable [tableSource]="tableSource" [tableColumns]="columns" [rowClass]="rowClassFn" [expandableDetailTemplate]="detailTpl" [expandedRow]="expandedRow" [showPaginator]="false" (rowExpandToggle)="onRowExpandToggle($event)"></app-generictable>
}

<ng-template #detailTpl let-row>
	<div class="sm-audit-detail">
		<div class="sm-audit-actions">
			@if (canManage) {
				<button mat-stroked-button type="button" (click)="auditRequested.emit([row.tableName])">
					{{ 'administration.sync-monitoring.audit.audit-table' | translate }}
				</button>
			}
			@if (canManage && row.skipped > 0) {
				<button mat-stroked-button type="button" (click)="openReplay(row)">
					{{ 'administration.sync-monitoring.audit.replay-table' | translate: { count: row.skipped } }}
				</button>
			}
			@if (canResetSync) {
				<button mat-stroked-button type="button" (click)="resetRequested.emit(row.tableName)">
					{{ 'administration.sync-monitoring.reset.row-action' | translate }}
				</button>
			}
		</div>

		@if (row.repairLabel) {
			<p class="sm-audit-explain">
				<strong>{{ 'administration.sync-monitoring.audit.columns.repair' | translate }}:</strong>
				{{ row.repairLabel }}
			</p>
		}
		@if (row.status !== 'differing' && row.status !== 'aligned') {
			<p class="sm-audit-explain">{{ 'administration.sync-monitoring.audit.explain.' + row.status | translate }}</p>
		}
		@if (row.audit?.replayedSkipped; as replay) {
			<p class="sm-audit-explain">
				{{ (replay.sent == null ? 'administration.sync-monitoring.audit.replay-pending' : 'administration.sync-monitoring.audit.replay-sent') | translate: { at: formatEpoch(replay.at), sent: replay.sent, superseded: replay.superseded ?? 0 } }}
			</p>
		}
		@if (row.audit?.runId) {
			<p class="sm-muted">
				{{ 'administration.sync-monitoring.audit.run' | translate: { run: row.audit.runId, trigger: row.audit.trigger ? ('administration.sync-monitoring.audit.triggers.' + row.audit.trigger | translate) : '—', at: row.lastAudit } }}
			</p>
		}

		<!-- Findings of the last completed run, one block per kind, 100 at a time -->
		@for (state of findings; track state.kind) {
			<div class="sm-audit-block">
				<h4>
					{{ 'administration.sync-monitoring.audit.kinds.' + state.kind | translate }}
					<span class="sm-muted">{{ 'administration.sync-monitoring.audit.showing' | translate: { shown: state.items.length, total: state.total ?? '—' } }}</span>
				</h4>
				@if (state.failed) {
					<div class="sm-error">{{ 'administration.sync-monitoring.errors.table-errors-load-failed' | translate }}</div>
				}
				@if (state.items.length > 0) {
					<table class="sm-audit-table">
						<thead>
							<tr>
								<th>{{ 'administration.sync-monitoring.audit.finding.key' | translate }}</th>
								<th>{{ 'administration.sync-monitoring.audit.finding.one-row-version' | translate }}</th>
								<th>{{ 'administration.sync-monitoring.audit.finding.engine-row-version' | translate }}</th>
								<th>{{ 'administration.sync-monitoring.audit.finding.outcome' | translate }}</th>
								<th>{{ 'administration.sync-monitoring.audit.finding.attempts' | translate }}</th>
							</tr>
						</thead>
						<tbody>
							@for (finding of state.items; track $index) {
								<tr>
									<td class="sm-mono">
										{{ finding.itemKey }}
										@if (isPinned(state.kind) && finding.auditS3ObjectVersion) {
											<button mat-button type="button" class="sm-audit-copy" (click)="copyVersion(finding.auditS3ObjectVersion)" [matTooltip]="finding.auditS3ObjectVersion">
												{{ 'administration.sync-monitoring.audit.copy-version' | translate }}
											</button>
										}
									</td>
									<td class="sm-mono">{{ rowVersion(finding.rowVersion) }}</td>
									<td class="sm-mono">{{ rowVersion(finding.engineRowVersion) }}</td>
									<td>
										@if (finding.repairOutcome) {
											<span class="sm-badge" [ngClass]="finding.repairOutcome === 'issued' ? 'sm-badge-ok' : 'sm-badge-warn'">{{ 'administration.sync-monitoring.audit.outcomes.' + finding.repairOutcome | translate }}</span>
										}
										@if (finding.repairKind) {
											<span class="sm-badge sm-badge-warn">{{ 'administration.sync-monitoring.audit.repair-kinds.' + finding.repairKind | translate }}</span>
										}
										@if (finding.observedAuditVersion) {
											<span>{{ 'administration.sync-monitoring.audit.finding.observed-version' | translate: { version: finding.observedAuditVersion } }}</span>
										}
										@if (finding.marked) {
											<span class="sm-badge sm-badge-warn">{{ 'administration.sync-monitoring.audit.finding.marked' | translate: { at: formatEpoch(finding.marked.expiresAt) } }}</span>
										}
										@if (finding.removalIssuedAt) {
											<span>{{ 'administration.sync-monitoring.audit.finding.removal-issued' | translate: { at: formatEpoch(finding.removalIssuedAt) } }}</span>
										}
									</td>
									<td>{{ finding.repairAttempts ?? '—' }}</td>
								</tr>
							}
						</tbody>
					</table>
				}
				@if (state.loading) {
					<mat-progress-spinner diameter="20" mode="indeterminate"></mat-progress-spinner>
				} @else if (state.next) {
					<button mat-button type="button" (click)="loadMore(state)">{{ 'administration.sync-monitoring.audit.load-more' | translate }}</button>
				}
			</div>
		}

		<!-- Clean-ups (fn-18): removal of extra engine rows, the off-version sweep and repair attempts -->
		@if (row.cleanup; as summary) {
			@if (summary.available === false) {
				<p class="sm-error">{{ summary.unavailableReason }}</p>
			} @else {
				@if (row.extra || summary.removalPass) {
					<div class="sm-audit-block">
						<h4>{{ 'administration.sync-monitoring.audit.removal.title' | translate }}</h4>
						<p class="sm-audit-explain">{{ 'administration.sync-monitoring.audit.removal.modes.' + (removalMode ?? 'reportOnly') | translate: { count: summary.permitted?.removal ?? 0 } }}</p>
						@if (summary.removalPass; as pass) {
							<p>
								{{
									'administration.sync-monitoring.audit.removal.pass'
										| translate
											: {
													outcome: pass.outcome,
													issued: pass.issued ?? 0,
													refused: pass.refused ?? 0,
													outstanding: pass.outstanding ?? 0,
													skipped: pass.exhaustedSkipped ?? 0,
													at: formatEpoch(pass.recordedAt),
											  }
								}}
							</p>
						}
					</div>
				}
				@if (row.offVersion || summary.sweepReport) {
					<div class="sm-audit-block">
						<h4>{{ 'administration.sync-monitoring.audit.sweep.title' | translate }}</h4>
						<p class="sm-audit-explain">{{ 'administration.sync-monitoring.audit.sweep.modes.' + (sweepMode ?? 'reportOnly') | translate: { count: summary.permitted?.sweep ?? 0, days: sweepWindowDays ?? '—' } }}</p>
						@if (summary.sweepReport; as sweep) {
							<p [class.sm-muted]="sweep.stale">
								{{
									'administration.sync-monitoring.audit.sweep.report'
										| translate
											: {
													marked: sweep.markedThisRun ?? 0,
													rescued: sweep.rescuedThisRun ?? 0,
													pending: sweep.pendingExpiryCount ?? 0,
													permitted: summary.permitted?.sweep ?? 0,
													windowEnds: formatEpoch(sweep.windowEndsAt),
											  }
								}}
							</p>
							@if (sweep.stale) {
								<p class="sm-audit-explain">{{ 'administration.sync-monitoring.audit.sweep.stale.' + (sweep.staleReason ?? 'run_superseded') | translate }}</p>
							}
						}
					</div>
				}

				@if (cleanupFailed) {
					<p class="sm-error">{{ 'administration.sync-monitoring.errors.cleanup-load-failed' | translate }}</p>
				}
				@if (cleanupLoading) {
					<mat-progress-spinner diameter="20" mode="indeterminate"></mat-progress-spinner>
				}
				@if (cleanup; as detail) {
					@if (detail.refusals?.length) {
						<div class="sm-audit-block">
							<h4>{{ 'administration.sync-monitoring.audit.refusals' | translate }}</h4>
							@for (refusal of detail.refusals; track $index) {
								<div>
									<span class="sm-badge">{{ refusal.kind }}</span>
									{{ refusal.reason }} ({{ refusal.count ?? 0 }})
									<span class="sm-mono sm-muted">{{ refusal.keys?.join(', ') }}</span>
								</div>
							}
						</div>
					}

					@if (detail.retrying?.items?.length || detail.exhausted?.items?.length) {
						<div class="sm-audit-block">
							<h4>{{ 'administration.sync-monitoring.audit.attempts.title' | translate }}</h4>
							<table class="sm-audit-table">
								<thead>
									<tr>
										<th>{{ 'administration.sync-monitoring.audit.attempts.kind' | translate }}</th>
										<th>{{ 'administration.sync-monitoring.audit.finding.key' | translate }}</th>
										<th>{{ 'administration.sync-monitoring.audit.finding.attempts' | translate }}</th>
										<th></th>
									</tr>
								</thead>
								<tbody>
									@for (item of attemptItems(detail); track $index) {
										<tr>
											<td>{{ item.kind }}</td>
											<td class="sm-mono">{{ item.itemKey }}</td>
											<td>
												{{ item.attemptsMade ?? 0 }} / {{ item.attemptLimit ?? 3 }}
												@if (item.exhausted) {
													<span class="sm-badge sm-badge-warn">{{ 'administration.sync-monitoring.audit.outcomes.exhausted' | translate }}</span>
												}
											</td>
											<td>
												@if (canManage && item.exhausted) {
													<button mat-button type="button" (click)="resetAttempts(row, item.itemKey)">{{ 'administration.sync-monitoring.audit.reset-attempts.button' | translate }}</button>
												}
											</td>
										</tr>
									}
								</tbody>
							</table>
							@if (canManage && detail.exhausted?.items?.length) {
								<button mat-stroked-button type="button" (click)="resetAttempts(row)">{{ 'administration.sync-monitoring.audit.reset-attempts.all' | translate }}</button>
							}
						</div>
					}

					<!-- Items the sweep marked for removal from One; keeping them clears their mark -->
					@for (group of markGroups; track group.version + group.runId) {
						<div class="sm-audit-block sm-audit-marks">
							<h4>
								{{ 'administration.sync-monitoring.audit.marks.title' | translate: { count: group.marks.length, version: group.version, expires: formatEpoch(group.expiresAt) } }}
								<span class="sm-muted sm-mono">{{ group.runId }}</span>
							</h4>
							<div class="sm-audit-mark-grid">
								@for (mark of group.marks; track mark.itemKey) {
									@if (canManage) {
										<mat-checkbox color="primary" [checked]="selectedMarks.has(mark.itemKey)" (change)="toggleMark(mark.itemKey, $event.checked)">
											<span class="sm-mono">{{ mark.itemKey }}{{ mark.confirmed === false ? ' *' : '' }}</span>
										</mat-checkbox>
									} @else {
										<span class="sm-mono">{{ mark.itemKey }}{{ mark.confirmed === false ? ' *' : '' }}</span>
									}
								}
							</div>
							@if (detail.marksCapped) {
								<p class="sm-muted">{{ 'administration.sync-monitoring.audit.marks.capped' | translate }}</p>
							}
							<p class="sm-muted">{{ 'administration.sync-monitoring.audit.marks.unconfirmed' | translate }}</p>
							@if (canManage) {
								<div class="sm-audit-actions">
									<button mat-stroked-button type="button" [disabled]="selectedIn(group).length === 0" (click)="keepMarks(row, group, false)">
										{{ 'administration.sync-monitoring.audit.marks.keep-selected' | translate: { count: selectedIn(group).length } }}
									</button>
									<button mat-stroked-button type="button" (click)="keepMarks(row, group, true)">
										{{ 'administration.sync-monitoring.audit.marks.keep-all' | translate: { count: group.marks.length } }}
									</button>
								</div>
							}
						</div>
					}
				}
			}
		}
	</div>
</ng-template>
